import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

interface MediaFactProps {
  label: string;
  children: ReactNode;
}

/** One labelled fact about a generated or uploaded asset. */
export const MediaFact = ({ label, children }: MediaFactProps) => (
  <div className="flex flex-col gap-3xs">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-sm text-foreground">{children}</span>
  </div>
);

MediaFact.displayName = 'MediaFact';

interface MediaMetadata {
  source?: string;
  promptUsed?: string;
  createdAt?: string;
  modelUsed?: string;
}

const EM_DASH = '—';

/** How an asset came to be: the prompt behind it, when, and with which model. */
export const MediaMetadataList = ({ metadata }: { metadata: MediaMetadata }) => {
  const { t } = useTranslation('audiences');

  const isGenerated = metadata.source === 'agent';

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-sm">
      {isGenerated && metadata.promptUsed && (
        <MediaFact label={t('audiences.profileImageSection.promptUsed')}>
          {metadata.promptUsed}
        </MediaFact>
      )}
      <div className="flex gap-lg">
        <MediaFact
          label={
            isGenerated
              ? t('audiences.profileImageSection.createdAt')
              : t('audiences.profileImageSection.uploadedAt')
          }
        >
          {metadata.createdAt ?? EM_DASH}
        </MediaFact>
        {isGenerated && metadata.modelUsed && (
          <MediaFact label={t('audiences.profileImageSection.modelUsed')}>
            {metadata.modelUsed}
          </MediaFact>
        )}
      </div>
    </div>
  );
};

MediaMetadataList.displayName = 'MediaMetadataList';
